import { jwtDecode } from "jwt-decode";
import { fetchUserProfile } from "./userService";
import axiosInstance from "./axiosInstance";
import { API_ENDPOINTS } from "./api/apiConfig";
import { prescriptionService, type PrescriptionRequest } from "./prescriptionService";
import { medicalHistoryService } from "./medicalHistoryService";

export interface AdditionalExamination {
  examName: string;
  reason: string;
  concernedDiagnostics: string;
  patientAdvice: string;
}

export interface PediatricsInfo {
  livingWith: string;
  hasPets: boolean;
  householdSmoking: boolean;
  childcareSchool: string;
  childSmoking: boolean;
  prenatalCareLocation: string;
  pregnancyComplications: string;
  tobaccoOrMedicationDuringPregnancy: string;
  birthLocation: string;
  deliveryType: string;
  birthWeight: number;
  gestationalAge: number;
  deliveryComplications: string;
  deliveryComplicationsDescription: string;
}

export interface GynecologyInfo {
  // Informations générales
  menopauseAge?: number;
  hysterectomyDate?: string | null;
  lastSmearTestDate?: string;
  lastMammographyDate?: string;
  pregnancyCount: number;
  hadAbnormalSmear: boolean;
  currentContraceptiveMethod: string;
  pastContraceptiveMethods: string;
  lastMenstrualDate: string;
  menstrualCycleLength: number;
  childrenCount: number;
  livingChildrenCount: number;
  deceasedChildrenCount: number;
  stillbornCount: number;
  miscarriageCount: number;
  deliveryType: string;

  // Informations sur la grossesse actuelle
  dateDebutGrossesse?: string;
  dateAccouchementPrevu?: string;
  grossesseDesiree?: boolean;
  grossesseMultiple?: boolean;
  nombreFoetus?: number;
  groupeSanguin?: string;
  rh?: string;
  rci?: boolean;
  menaceAccouchementPremature?: boolean;
  diabeteGestationnel?: boolean;
  hypertensionArterielle?: boolean;
  preeclampsie?: boolean;
  autresPathologies?: string;
  traitementEnCours?: string;
  allergies?: string;
  antecedentsObstetricaux?: string;
  commentaires?: string;
}

export interface MedicalRecordRequest {
  id?: number;
  symptoms: string;
  symptomDuration: string;
  currentMedication: string;
  preexistingCondition: string;
  familyHistory: string;
  allergies: string;
  assessment: string;
  recommendation: string;
  doctorId: number;
  patientId: number | null;
  consultationFee: number;
  amountPaid: number;
  amountDue: number;
  dateCreation?: string;
  systemsReview: string;
  observations: string;
  diagnostic: string;
  medicalHistorySummary?: string | null;
  lastAnnualCheckupDate: string;
  specialtyType:
  | "GENERAL"
  | "PEDIATRICS"
  | "GYNECOLOGY"
  | "CARDIOLOGY"
  | "DERMATOLOGY";
  gynecologyInfo?: GynecologyInfo | null;
  pediatricsInfo?: PediatricsInfo | null;
  additionalExaminations: AdditionalExamination[];
  prescriptionItems?: any[];
  prescriptionUrl?: string | null;
  resultUrl?: string | null;
  appointmentId?: number | null;
  medicalHistoryId?: number | null;
}

export interface MedicalRecordResponse extends MedicalRecordRequest {
  id: number;
  dateCreation: string;
}

interface JwtPayload {
  exp: number;
  sub: string;
  authorities: string[];
  iat: number;
}

export interface MedicationLite {
  id?: number;
  name?: string;
  dose?: number | string;
  doseUnit?: string;
  frequency?: number | string;
  interval?: string;
  duration?: number | string;
  durationUnit?: string;
  quantity?: number | string;
  medicamentId?: number; // Ajouté pour le lien avec la table des médicaments
  instructions?: string;
}

type AnalyseLite = {
  label?: string;
  nom?: string;
  reason?: string;
  diagnostics?: string;
  conseils?: string;
};

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const validateTokenAndRole = (token: string): JwtPayload => {
  if (!token) {
    throw new Error("Token d'authentification manquant");
  }

  if (isTokenExpired(token)) {
    throw new Error("Token d'authentification expiré");
  }

  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (!decoded.authorities ||
      (!decoded.authorities.includes("ROLE_DOCTOR") && !decoded.authorities.includes("DOCTOR"))) {
      throw new Error("Permissions insuffisantes - rôle DOCTOR requis");
    }
    return decoded;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Token invalide");
  }
};

export class ConsultationService {
  private async getCurrentDoctorId(): Promise<number> {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!token || !userId) {
      throw new Error("Token d'authentification ou ID utilisateur manquant");
    }

    validateTokenAndRole(token);

    try {
      const userData = await fetchUserProfile(userId);
      if (!userData.id) {
        throw new Error("ID du docteur introuvable dans le profil utilisateur");
      }

      const doctorId = parseInt(userData.id, 10);
      if (isNaN(doctorId) || doctorId <= 0) {
        throw new Error("ID du docteur invalide");
      }

      return doctorId;
    } catch (error) {
      console.error("consultationService: Error fetching doctor ID:", error);
      throw new Error("Impossible de récupérer l'ID du docteur");
    }
  }

  private mapFormDataToMedicalRecord(
    formData: any,
    doctorId: number,
    patientId: number,
    consultationFee: number = 0,
    amountPaid: number = 0
  ): MedicalRecordRequest {
    if (!patientId || patientId <= 0) {
      throw new Error("ID du patient invalide");
    }
    if (!doctorId || doctorId <= 0) {
      throw new Error("ID du docteur invalide");
    }

    const symptoms = this.extractSymptoms(formData);
    const symptomDuration = this.extractSymptomDuration(formData);
    const familyHistory = this.buildFamilyHistory(
      formData.antecedentsFamiliaux
    );
    const preexistingCondition = this.buildPreexistingConditions(
      formData.conditionsMedicales
    );
    const allergies = this.buildAllergies(formData.conditionsMedicales);
    const specialtyType = this.determineSpecialtyType(
      formData.patientInfo.motifConsultation
    );
    const pediatricsInfo =
      specialtyType === "PEDIATRICS"
        ? this.buildPediatricsInfo(formData)
        : null;
    const gynecologyInfo =
      specialtyType === "GYNECOLOGY"
        ? this.buildGynecologyInfo(formData)
        : null;
    const systemsReview = this.buildSystemsReview(
      formData.observationsSystemes
    );

    const medsArray: MedicationLite[] = Array.isArray(
      (formData as any).medications
    )
      ? (formData as any).medications
      : [];
    const currentMedicationStr =
      medsArray.length > 0
        ? medsArray
          .map((m, i) => {
            const parts: string[] = [];
            if (m.dose && m.doseUnit) parts.push(`${m.dose} ${m.doseUnit}`);
            if (m.frequency && m.interval)
              parts.push(`${m.frequency} ${m.interval}`);
            if (m.duration && m.durationUnit)
              parts.push(`${m.duration} ${m.durationUnit}`);
            return `${i + 1}. ${m.name}${parts.length ? " (" + parts.join(", ") + ")" : ""
              }`;
          })
          .join("; ")
        : (formData as any).medicaments || "Aucun";

    const additionalExaminations: AdditionalExamination[] = Array.isArray(
      (formData as any).analyses
    )
      ? (formData as any).analyses.map((a: AnalyseLite) => ({
        examName: a.label || a.nom || "",
        reason: a.reason || "",
        concernedDiagnostics: a.diagnostics || "",
        patientAdvice: a.conseils || "",
      }))
      : [];

    const prescriptionItems: any[] = medsArray.map((m: MedicationLite) => ({
      name: m.name || "",
      quantity: typeof m.quantity === 'number' ? m.quantity : parseInt(m.quantity as any, 10) || 1,
      quantityUnit: "Unités",
      dose: m.dose?.toString() || "",
      doseUnit: m.doseUnit || "",
      frequency: m.frequency?.toString() || "",
      interval: m.interval || "",
      duration: typeof m.duration === 'number' ? m.duration : parseInt(m.duration as any, 10) || 0,
      durationUnit: m.durationUnit || "",
      instructions: m.instructions || ""
    }));

    return {
      symptoms,
      symptomDuration,
      currentMedication: currentMedicationStr,
      preexistingCondition,
      familyHistory,
      allergies,
      assessment: formData.consultationActuelle?.observations || "",
      recommendation: formData.consultationActuelle?.recommandations || "",
      doctorId,
      patientId,
      consultationFee,
      amountPaid,
      amountDue: consultationFee - amountPaid,
      systemsReview,
      observations: formData.consultationActuelle?.observations || "",
      diagnostic: formData.consultationActuelle?.diagnostics || "",
      lastAnnualCheckupDate:
        formData.dateDernierBilan || new Date().toISOString().split("T")[0],
      specialtyType,
      gynecologyInfo,
      pediatricsInfo,
      additionalExaminations,
      prescriptionItems
    };
  }


  private extractSymptoms(formData: any): string {
    const symptoms: string[] = [];
    Object.entries(formData.conditionsMedicales || {}).forEach(
      ([key, value]) => {
        if (value === true) {
          symptoms.push(this.translateMedicalCondition(key));
        }
      }
    );
    Object.entries(formData.observationsSystemes || {}).forEach(
      ([key, value]) => {
        if (value === true) {
          symptoms.push(this.translateSystemObservation(key));
        }
      }
    );
    if (formData.patientInfo?.motifConsultation) {
      symptoms.push(formData.patientInfo.motifConsultation);
    }
    return symptoms.length > 0
      ? symptoms.join(", ")
      : "Consultation de routine";
  }

  private extractSymptomDuration(_formData: any): string {
    return "Non spécifié";
  }

  private buildFamilyHistory(antecedentsFamiliaux: any): string {
    const history: string[] = [];
    Object.entries(antecedentsFamiliaux || {}).forEach(([key, value]) => {
      if (value === true) {
        history.push(this.translateMedicalCondition(key));
      }
    });
    return history.length > 0
      ? history.join(", ")
      : "Aucun antécédent familial notable";
  }

  private buildPreexistingConditions(conditionsMedicales: any): string {
    const conditions: string[] = [];
    Object.entries(conditionsMedicales || {}).forEach(([key, value]) => {
      if (value === true) {
        conditions.push(this.translateMedicalCondition(key));
      }
    });
    return conditions.length > 0
      ? conditions.join(", ")
      : "Aucune condition préexistante";
  }

  private buildAllergies(conditionsMedicales: any): string {
    if (conditionsMedicales?.allergie) {
      return conditionsMedicales.autre || "Allergies non spécifiées";
    }
    return "Aucune allergie connue";
  }

  private buildSystemsReview(observationsSystemes: any): string {
    const observations: string[] = [];
    Object.entries(observationsSystemes || {}).forEach(([key, value]) => {
      if (value === true) {
        observations.push(this.translateSystemObservation(key));
      }
    });
    return observations.length > 0
      ? observations.join(", ")
      : "Revue des systèmes normale";
  }

  private determineSpecialtyType(
    motifConsultation: string
  ): MedicalRecordRequest["specialtyType"] {
    const motif = (motifConsultation || "").toLowerCase();
    if (motif.includes("pediatric") || motif.includes("pédiatrie")) {
      return "PEDIATRICS";
    }
    if (motif.includes("gynecology") || motif.includes("gynécologie")) {
      return "GYNECOLOGY";
    }
    if (motif.includes("cardio")) {
      return "CARDIOLOGY";
    }
    if (motif.includes("dermat")) {
      return "DERMATOLOGY";
    }
    return "GENERAL";
  }

  private buildPediatricsInfo(formData: any): PediatricsInfo | null {
    return {
      livingWith: "Parents",
      hasPets: formData.vieSociale?.animauxMaison || false,
      householdSmoking: formData.vieSociale?.fumeurMaison || false,
      childcareSchool: "Non spécifié",
      childSmoking: false,
      prenatalCareLocation: "Non spécifié",
      pregnancyComplications: "Non spécifié",
      tobaccoOrMedicationDuringPregnancy: "Non spécifié",
      birthLocation: "Non spécifié",
      deliveryType: "Normal",
      birthWeight: 0,
      gestationalAge: 0,
      deliveryComplications: "Non spécifié",
      deliveryComplicationsDescription: "Non spécifié",
    };
  }

  private buildGynecologyInfo(formData: any): GynecologyInfo | null {
    const gynecologyInfo: GynecologyInfo = {
      menopauseAge: 0,
      hysterectomyDate: null,
      lastSmearTestDate: new Date().toISOString().split("T")[0],
      lastMammographyDate: new Date().toISOString().split("T")[0],
      pregnancyCount: 0,
      hadAbnormalSmear: false,
      currentContraceptiveMethod: "Non spécifié",
      pastContraceptiveMethods: "Non spécifié",
      lastMenstrualDate: new Date().toISOString().split("T")[0],
      menstrualCycleLength: 28,
      childrenCount: 0,
      livingChildrenCount: 0,
      deceasedChildrenCount: 0,
      stillbornCount: 0,
      miscarriageCount: 0,
      deliveryType: "Normal",
    };

    // Mise à jour avec les données du formulaire si disponibles
    if (formData.informationGynecologique) {
      const info = formData.informationGynecologique;

      // Informations générales
      if (info.ageMenopause) gynecologyInfo.menopauseAge = parseInt(info.ageMenopause, 10) || 0;
      if (info.dateHysterectomie) gynecologyInfo.hysterectomyDate = info.dateHysterectomie;
      if (info.dernierFrottis) gynecologyInfo.lastSmearTestDate = info.dernierFrottis;
      if (info.derniereMammographie) gynecologyInfo.lastMammographyDate = info.derniereMammographie;
      if (info.nombreGrossesses) gynecologyInfo.pregnancyCount = parseInt(info.nombreGrossesses, 10) || 0;
      if (info.frottisAnormal !== undefined) gynecologyInfo.hadAbnormalSmear = info.frottisAnormal;
      if (info.methodeContraceptiveActuelle) gynecologyInfo.currentContraceptiveMethod = info.methodeContraceptiveActuelle;
      if (info.methodesContraceptivesPassees) gynecologyInfo.pastContraceptiveMethods = info.methodesContraceptivesPassees;
      if (info.dateDernierRegle) gynecologyInfo.lastMenstrualDate = info.dateDernierRegle;
      if (info.cycleTousLesJours) gynecologyInfo.menstrualCycleLength = parseInt(info.cycleTousLesJours, 10) || 28;
      if (info.nombreEnfants) gynecologyInfo.childrenCount = parseInt(info.nombreEnfants, 10) || 0;
      if (info.nombreEnfantsVivants) gynecologyInfo.livingChildrenCount = parseInt(info.nombreEnfantsVivants, 10) || 0;
      if (info.nombreEnfantsDecedes) gynecologyInfo.deceasedChildrenCount = parseInt(info.nombreEnfantsDecedes, 10) || 0;
      if (info.nombreMortNes) gynecologyInfo.stillbornCount = parseInt(info.nombreMortNes, 10) || 0;
      if (info.nombreFausseCouches) gynecologyInfo.miscarriageCount = parseInt(info.nombreFausseCouches, 10) || 0;
      if (info.typeAccouchement) gynecologyInfo.deliveryType = info.typeAccouchement;

      // Informations sur la grossesse actuelle
      if (info.grossesseActuelle) {
        const grossesse = info.grossesseActuelle;
        if (grossesse.dateDebutGrossesse) gynecologyInfo.dateDebutGrossesse = grossesse.dateDebutGrossesse;
        if (grossesse.dateAccouchementPrevu) gynecologyInfo.dateAccouchementPrevu = grossesse.dateAccouchementPrevu;
        if (grossesse.grossesseDesiree !== undefined) gynecologyInfo.grossesseDesiree = grossesse.grossesseDesiree;
        if (grossesse.grossesseMultiple !== undefined) gynecologyInfo.grossesseMultiple = grossesse.grossesseMultiple;
        if (grossesse.nombreFoetus) gynecologyInfo.nombreFoetus = parseInt(grossesse.nombreFoetus, 10) || 1;
        if (grossesse.groupeSanguin) gynecologyInfo.groupeSanguin = grossesse.groupeSanguin;
        if (grossesse.rh) gynecologyInfo.rh = grossesse.rh;
        if (grossesse.rci !== undefined) gynecologyInfo.rci = grossesse.rci;
        if (grossesse.menaceAccouchementPremature !== undefined) gynecologyInfo.menaceAccouchementPremature = grossesse.menaceAccouchementPremature;
        if (grossesse.diabeteGestationnel !== undefined) gynecologyInfo.diabeteGestationnel = grossesse.diabeteGestationnel;
        if (grossesse.hypertensionArterielle !== undefined) gynecologyInfo.hypertensionArterielle = grossesse.hypertensionArterielle;
        if (grossesse.preeclampsie !== undefined) gynecologyInfo.preeclampsie = grossesse.preeclampsie;
        if (grossesse.autresPathologies) gynecologyInfo.autresPathologies = grossesse.autresPathologies;
        if (grossesse.traitementEnCours) gynecologyInfo.traitementEnCours = grossesse.traitementEnCours;
        if (grossesse.allergies) gynecologyInfo.allergies = grossesse.allergies;
        if (grossesse.antecedentsObstetricaux) gynecologyInfo.antecedentsObstetricaux = grossesse.antecedentsObstetricaux;
        if (grossesse.commentaires) gynecologyInfo.commentaires = grossesse.commentaires;
      }
    }

    return gynecologyInfo;
  }

  private translateMedicalCondition(key: string): string {
    const translations: { [key: string]: string } = {
      hypertensionArterielle: "Hypertension artérielle",
      maladieCardiaque: "Maladie cardiaque",
      cholesterolEleve: "Cholestérol élevé",
      avc: "AVC",
      diabete: "Diabète",
      maladieThyroidienne: "Maladie thyroïdienne",
      drepanocytose: "Drépanocytose",
      neurologique: "Problème neurologique",
      toux: "Toux",
      douleursMammaires: "Douleurs mammaires",
      anomalieUterine: "Anomalie utérine",
      respirationSifflante: "Respiration sifflante",
      maladiesInfectieuses: "Maladies infectieuses",
      tuberculose: "Tuberculose",
      asthme: "Asthme",
      arthrite: "Arthrite",
      cancer: "Cancer",
      anemie: "Anémie",
      defautGenetique: "Défaut génétique",
      sinusite: "Sinusite",
      vertige: "Vertige",
      problemesMentaux: "Problèmes mentaux",
      allergie: "Allergie",
      chirurgieGyneco: "Chirurgie gynécologique",
      infertilite: "Infertilité",
      vih: "VIH",
      cyclesIrreguliers: "Cycles irréguliers",
      fatigue: "Fatigue",
    };
    return translations[key] || key;
  }

  private translateSystemObservation(key: string): string {
    const translations: { [key: string]: string } = {
      fatigue: "Fatigue",
      malNutrition: "Malnutrition",
      pertePoids: "Perte de poids",
      fievresRecurrentes: "Fièvres récurrentes",
      difficulteMarcher: "Difficulté à marcher",
      hyperactivite: "Hyperactivité",
      ecoulementOculaire: "Écoulement oculaire",
      infectionsOreilleChroniques: "Infections chroniques de l'oreille",
      sangUrine: "Sang dans les urines",
      myopie: "Myopie",
      depression: "Dépression",
      douleurAbdominale: "Douleur abdominale",
      problemesAudition: "Problèmes d'audition",
      mauxGorge: "Maux de gorge",
      vertiges: "Vertiges",
      anxiete: "Anxiété",
      ganglionsLymphatiques: "Ganglions lymphatiques",
      congestionChronique: "Congestion chronique",
      insomnie: "Insomnie",
      sangMucus: "Sang dans les mucus",
      ecoulementNasal: "Écoulement nasal",
      anemie: "Anémie",
      douleurPoitrine: "Douleur à la poitrine",
      essoufflement: "Essoufflement",
      nauseesVomissements: "Nausées et vomissements",
      diarrheeConstipation: "Diarrhée/Constipation",
      douleursMusculaires: "Douleurs musculaires",
    };
    return translations[key] || key;
  }

  async createConsultation(
    formData: any,
    doctorId: number,
    patientId: number,
    consultationFee: number = 0,
    amountPaid: number = 0,
    medications?: any[]
  ): Promise<{ medicalRecord: MedicalRecordResponse; prescription?: any; medicalHistory?: any }> {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      validateTokenAndRole(token);

      if (!patientId || patientId <= 0) {
        throw new Error("ID du patient invalide");
      }
      if (!doctorId || doctorId <= 0) {
        throw new Error("ID du docteur invalide");
      }

      const medicalRecord = this.mapFormDataToMedicalRecord(
        formData,
        doctorId,
        patientId,
        consultationFee,
        amountPaid
      );


      const response = await axiosInstance.post(
        API_ENDPOINTS.MEDICAL_RECORDS.CREATE,
        medicalRecord
      );


      if (response.status < 200 || response.status >= 300) {
        let errorMessage = `Erreur HTTP: ${response.status} - ${response.statusText || "Unknown error"
          }`;

        if (response.data?.message) {
          errorMessage = response.data.message;
        } else if (response.data?.error) {
          errorMessage = response.data.error;
        }

        switch (response.status) {
          case 403:
            errorMessage = "Accès interdit. Vérifiez vos permissions.";
            break;
          case 401:
            errorMessage = "Non autorisé. Votre session a peut-être expiré.";
            break;
          case 422:
            errorMessage =
              "Données invalides. Vérifiez les informations saisies.";
            break;
          case 404:
            errorMessage = "Patient ou ressource non trouvé.";
            break;
          case 500:
            errorMessage =
              "Erreur interne du serveur. Contactez l'administrateur.";
            break;
        }

        throw new Error(errorMessage);
      }

      const result: MedicalRecordResponse = response.data;

      // Créer les antécédents médicaux
      let medicalHistory = null;
      try {
        // Extraire les conditions médicales cochées
        const conditionsMedicales = formData.conditionsMedicales || {};
        const antecedentsFamiliaux = formData.antecedentsFamiliaux || {};

        // Construire les chaînes de caractères pour les conditions
        const allergiesList = conditionsMedicales.allergie ? ['Allergies'] : [];
        const chronicDiseasesList = [];
        if (conditionsMedicales.hypertensionArterielle) chronicDiseasesList.push('Hypertension artérielle');
        if (conditionsMedicales.diabete) chronicDiseasesList.push('Diabète');
        if (conditionsMedicales.maladieCardiaque) chronicDiseasesList.push('Maladie cardiaque');
        if (conditionsMedicales.avc) chronicDiseasesList.push('AVC');
        if (conditionsMedicales.maladieThyroidienne) chronicDiseasesList.push('Maladie thyroïdienne');
        if (conditionsMedicales.neurologique) chronicDiseasesList.push('Problème neurologique');
        if (conditionsMedicales.drepanocytose) chronicDiseasesList.push('Drépanocytose');
        if (conditionsMedicales.cancer) chronicDiseasesList.push('Cancer');
        if (conditionsMedicales.arthrite) chronicDiseasesList.push('Arthrite');
        if (conditionsMedicales.anemie) chronicDiseasesList.push('Anémie');

        const pastSurgeriesList = [];
        if (conditionsMedicales.chirurgieGyneco) pastSurgeriesList.push('Chirurgie gynécologique');

        const familyHistoryList = [];
        if (antecedentsFamiliaux.hypertensionArterielle) familyHistoryList.push('Hypertension artérielle');
        if (antecedentsFamiliaux.diabete) familyHistoryList.push('Diabète');
        if (antecedentsFamiliaux.avc) familyHistoryList.push('AVC');
        if (antecedentsFamiliaux.maladieCardiaque) familyHistoryList.push('Maladie cardiaque');
        if (antecedentsFamiliaux.asthme) familyHistoryList.push('Asthme');
        if (antecedentsFamiliaux.cancer) familyHistoryList.push('Cancer');
        if (antecedentsFamiliaux.arthrite) familyHistoryList.push('Arthrite');

        const medicalHistoryData = {
          patientId,
          allergies: allergiesList.join(', ') || (formData.allergies || ''),
          chronicDiseases: chronicDiseasesList.join(', ') || '',
          currentMedications: formData.consultationActuelle?.traitements || formData.medicaments || '',
          pastSurgeries: pastSurgeriesList.join(', ') || '',
          familyHistory: familyHistoryList.join(', ') || '',
          pregnancyHistory: conditionsMedicales.infertilite ? 'Infertilité' : '',
          substanceUse: formData.vieSociale?.fumeur ? 'Fumeur' : '',
          bloodType: '',
          vaccinationHistory: '',
          drugAllergies: allergiesList.join(', ') || (formData.allergies || ''),
          emergencyContactName: '',
          emergencyContactPhone: ''
        };


        medicalHistory = await medicalHistoryService.createMedicalHistory(medicalHistoryData);
      } catch (medicalHistoryError) {
        console.error('Erreur lors de la création des antécédents médicaux:', medicalHistoryError);
        // Ne pas échouer la création de la consultation à cause de l'échec des antécédents médicaux
      }

      // Créer la prescription si des médicaments sont fournis
      let prescription = null;
      if (medications && medications.length > 0) {
        try {
          const prescriptionData: PrescriptionRequest = {
            doctorId,
            patientId,
            medicalRecordId: result.id,
            issueDate: new Date().toISOString().split('T')[0], // Format YYYY-MM-DD
            items: medications
              .filter(med => med.medicamentId && med.medicamentId > 0) // Ne garder que les médicaments avec un ID valide
              .map(med => ({
                medicamentId: med.medicamentId!,
                quantity: med.quantity || 1,
                dosage: `${med.dose || ''} ${med.doseUnit || ''}`.trim(),
                frequency: `${med.frequency || ''} ${med.interval || ''}`.trim(),
                route: 'Oral', // Valeur par défaut
                form: 'Comprimé', // Valeur par défaut
                specialInstructions: med.instructions || ''
              }))
          };

          if (prescriptionData.items.length > 0) {
            prescription = await prescriptionService.createPrescription(prescriptionData);
          } else {
            // No prescription items
          }
        } catch (prescriptionError) {
          console.error('Erreur lors de la création de la prescription:', prescriptionError);
          // Ne pas échouer la création de la consultation à cause de l'échec de la prescription
        }
      }

      return { medicalRecord: result, prescription, medicalHistory };
    } catch (error) {
      console.error("consultationService: Error creating consultation:", error);
      throw error;
    }
  }

  async createConsultationAuto(
    formData: any,
    patientId: number,
    consultationFee: number = 0,
    amountPaid: number = 0
  ): Promise<{ medicalRecord: MedicalRecordResponse; prescription?: any }> {
    try {
      const doctorId = await this.getCurrentDoctorId();

      // Extraire les médicaments des données du formulaire pour les passer séparément
      const medications = formData.medications || [];

      // On garde medications dans formData pour que mapFormDataToMedicalRecord puisse l'utiliser
      // Mais on le passe aussi séparément pour la création de prescription
      return await this.createConsultation(
        formData, // Ne PAS retirer medications
        doctorId,
        patientId,
        consultationFee,
        amountPaid,
        medications
      );
    } catch (error) {
      console.error(
        "consultationService: Error in createConsultationAuto:",
        error
      );
      throw error;
    }
  }

  async getConsultation(
    consultationId: number
  ): Promise<MedicalRecordResponse> {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      validateTokenAndRole(token);

      const response = await axiosInstance.get(
        API_ENDPOINTS.MEDICAL_RECORDS.GET_BY_ID(consultationId)
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          response.data?.message || `Erreur HTTP: ${response.status}`
        );
      }

      return response.data;
    } catch (error) {
      console.error("consultationService: Error fetching consultation:", error);
      throw error;
    }
  }

  async updateConsultation(
    consultationId: number,
    formData: any,
    doctorId: number,
    patientId: number,
    consultationFee: number = 0,
    amountPaid: number = 0
  ): Promise<MedicalRecordResponse> {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      validateTokenAndRole(token);

      const medicalRecord = this.mapFormDataToMedicalRecord(
        formData,
        doctorId,
        patientId,
        consultationFee,
        amountPaid
      );


      const response = await axiosInstance.put(
        API_ENDPOINTS.MEDICAL_RECORDS.UPDATE(consultationId),
        { ...medicalRecord, id: consultationId }
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          response.data?.message || `Erreur HTTP: ${response.status}`
        );
      }

      return response.data;
    } catch (error) {
      console.error("consultationService: Error updating consultation:", error);
      throw error;
    }
  }

  async deleteConsultation(consultationId: number): Promise<void> {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      validateTokenAndRole(token);

      const response = await axiosInstance.delete(
        API_ENDPOINTS.MEDICAL_RECORDS.DELETE(consultationId)
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          response.data?.message || `Erreur HTTP: ${response.status}`
        );
      }
    } catch (error) {
      console.error("consultationService: Error deleting consultation:", error);
      throw error;
    }
  }

  async markConstanteAsConsulted(
    constanteId: number,
    consultationId: number,
    _reason: string
  ): Promise<void> {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      validateTokenAndRole(token);

      // Récupérer d'abord la constante existante
      const getResponse = await axiosInstance.get(
        API_ENDPOINTS.CONSTANTES.GET_BY_ID(constanteId)
      );

      if (getResponse.status < 200 || getResponse.status >= 300) {
        throw new Error(
          getResponse.data?.message || `Erreur HTTP lors de la récupération de la constante: ${getResponse.status}`
        );
      }

      const existingConstante = getResponse.data;

      // Mettre à jour les champs nécessaires
      const updatedConstante = {
        ...existingConstante,
        hasConsultation: true,
        consultationId,
        consultationDate: new Date().toISOString(),
        reason: _reason,
        status: 'APPROVED' // Assurez-vous que c'est une valeur valide selon votre énumération Status
      };

      // Envoyer la mise à jour avec la méthode PUT standard
      const response = await axiosInstance.put(
        API_ENDPOINTS.CONSTANTES.UPDATE(constanteId),
        updatedConstante
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          response.data?.message || `Erreur HTTP: ${response.status}`
        );
      }
    } catch (error) {
      console.error(
        "consultationService: Error marking constante as consulted:",
        error
      );
      throw error;
    }
  }
}

export const consultationService = new ConsultationService();
